import { saveAs } from "file-saver"
import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HeadingLevel,
  Packer,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TabStopPosition,
  TabStopType,
  TextRun,
  WidthType,
  convertInchesToTwip,
} from "docx"
import {
  FONT,
  OIO_PAGE,
  SIZES,
  SPACING,
  createFormattedRuns,
  elementToDocx,
  oioDocumentStyles,
  oioLegalNumbering,
  parseMarkdownContent,
} from "../../oio/lib/oio-formatter"
import {
  CAPTION_PLACEHOLDERS,
  todayDmy,
  canonicalIds,
  planNumbering,
  sectionHeadings,
  stripTypedNumber,
  type PlannedBlock,
  type SectionHeading,
  type SectionTitles,
} from "../sections"

/** Exact header/footer copy for simulator drafts (global constraints). */
export const SIMULATED_BANNER = "SIMULATED DRAFT — NOT AN ORDER"

export interface OioSimSection {
  id: string
  markdown: string
}

export interface OioSimDocMeta {
  title: string
  /** Allegation titles from GET /draft, for the "Issue n: <title>" headings. */
  titles?: SectionTitles
  /** Letterhead line, e.g. "Office of the Assistant Commissioner of Central Tax, Sangareddy Division". */
  office?: string
  /** The file's C.No. */
  cNo?: string
  date?: string
  /** Noticee for the running header; defaults to the title. */
  noticee?: string
}

type Block = Paragraph | Table
const [OIO_NO, DIN] = CAPTION_PLACEHOLDERS

const plain = (text: string, opts: { align?: (typeof AlignmentType)[keyof typeof AlignmentType]; bold?: boolean; after?: number } = {}) =>
  new Paragraph({
    children: [new TextRun({ text, font: FONT, size: SIZES.BODY, bold: opts.bold })],
    alignment: opts.align ?? AlignmentType.LEFT,
    spacing: { before: SPACING.PARA_BEFORE, after: opts.after ?? SPACING.PARA_AFTER, line: SPACING.LINE },
  })

/** D1: letterhead table, C.No./Date row, ORDER-IN-ORIGINAL line, "Passed by" line and right-aligned DIN. */
export function captionBlocks(meta: OioSimDocMeta): Block[] {
  const office = meta.office?.trim() || "[Office]"
  const none = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }
  return [
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              margins: { top: 80, bottom: 80, left: 80, right: 80 },
              children: [plain(office.toUpperCase(), { align: AlignmentType.CENTER, bold: true })],
            }),
          ],
        }),
      ],
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `C. No. ${meta.cNo?.trim() || "[C. No.]"}`, font: FONT, size: SIZES.BODY }),
        new TextRun({ text: `\tDate: ${meta.date?.trim() || todayDmy()}`, font: FONT, size: SIZES.BODY }),
      ],
      tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
      spacing: { before: SPACING.HEADING_BEFORE, after: SPACING.PARA_AFTER, line: SPACING.LINE },
      border: { top: none, bottom: none, left: none, right: none },
    }),
    plain(`ORDER-IN-ORIGINAL No. ${OIO_NO}`, { align: AlignmentType.CENTER, bold: true }),
    plain(`(Passed by [Name of Adjudicating Authority], [Designation], ${office})`, { align: AlignmentType.CENTER, bold: true }),
    plain(`DIN: ${DIN}`, { align: AlignmentType.RIGHT }),
  ]
}

/** D2: real Word heading styles; PREAMBLE and ORDER are centred and underlined. */
function headingBlock(h: { text: string; level: 1 | 2; center?: boolean }): Paragraph {
  return new Paragraph({
    heading: h.level === 1 ? HeadingLevel.HEADING_1 : HeadingLevel.HEADING_2,
    children: createFormattedRuns(h.text, {
      bold: true,
      size: h.level === 1 ? SIZES.HEADING : SIZES.BODY,
      allCaps: h.level === 1,
      underline: h.center ? { type: "single" } : undefined,
    }),
    alignment: h.center ? AlignmentType.CENTER : AlignmentType.LEFT,
    spacing: { before: SPACING.HEADING_BEFORE, after: SPACING.HEADING_AFTER, line: SPACING.LINE },
  })
}

const asDocx = (md: string): Block[] => parseMarkdownContent(md, { explicitHeadingsOnly: true }).flatMap(elementToDocx)

const continuation = (line: string): Paragraph =>
  new Paragraph({
    children: createFormattedRuns(line, { size: SIZES.BODY }),
    alignment: AlignmentType.JUSTIFIED,
    spacing: { before: 0, after: SPACING.PARA_AFTER, line: SPACING.LINE },
    indent: { left: convertInchesToTwip(0.75) },
  })

/** One planned markdown block as Word paragraphs. */
function blockToDocx(id: string, b: PlannedBlock): Block[] {
  switch (b.kind) {
    case "heading":
    case "table":
      return asDocx(b.lines.join("\n"))
    case "quote": {
      // D6: quoted statute extracts are indented, justified and in quotation marks.
      const text = b.lines.join(" ").trim()
      const quoted = /^["“]/.test(text) ? text : `“${text}”`
      return [
        new Paragraph({
          children: createFormattedRuns(quoted, { size: SIZES.BODY }),
          alignment: AlignmentType.JUSTIFIED,
          spacing: { before: SPACING.PARA_BEFORE, after: SPACING.PARA_AFTER, line: SPACING.LINE },
          indent: { left: convertInchesToTwip(0.5), right: convertInchesToTwip(0.5) },
        }),
      ]
    }
    case "list":
      return b.lines.flatMap((item, i) => {
        const [first, ...rest] = item.split("\n")
        const label = b.labels[i] ?? `${i + 1}`
        const numbered = elementToDocx({
          type: "numbered",
          number: label,
          content: b.labels[i] ? stripTypedNumber(first) : first,
          level: label.includes(".") ? 1 : 0,
        })
        // A Hindi/English pair is one item: the second line sits directly under the first, without a number.
        return [...numbered, ...rest.map(continuation)]
      })
    case "para": {
      const text = b.lines.join(" ")
      if (b.labels[0]) {
        return elementToDocx({
          type: "numbered",
          number: b.labels[0],
          content: stripTypedNumber(text),
          level: b.labels[0].includes(".") ? 1 : 0,
        })
      }
      if (id === "subject") {
        return [
          new Paragraph({
            children: createFormattedRuns(text.replace(/\*+/g, ""), { bold: true, size: SIZES.BODY }),
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: SPACING.PARA_BEFORE, after: SPACING.PARA_AFTER, line: SPACING.LINE },
          }),
        ]
      }
      if (b.tail) {
        // D5: the To block, copy-to list and signature lines are left-aligned, line by line.
        return b.lines.flatMap((line) =>
          /^\[.*\]$|^\(.*\)$/.test(line)
            ? elementToDocx({ type: "signature", content: line })
            : [
                new Paragraph({
                  children: createFormattedRuns(line, { size: SIZES.BODY }),
                  alignment: AlignmentType.LEFT,
                  spacing: { before: 20, after: 20, line: SPACING.LINE },
                }),
              ],
        )
      }
      return b.lines.flatMap(asDocx)
    }
  }
}

/**
 * Lays out the simulator's ordered markdown sections as one OIO-styled
 * document. Styling, markdown parsing and table rendering come from
 * `features/oio/lib/oio-formatter.ts`; the caption, headings, numbering, header and footer are simulator-specific.
 */
export function buildOioSimDocx(sections: OioSimSection[], meta: OioSimDocMeta): Document {
  // Section markdown is body only: canonical headings, the caption and one continuous paragraph counter are added here, as on the page.
  const order = canonicalIds(sections.map((s) => s.id))
  const byId = new Map(sections.map((s) => [s.id, s.markdown]))
  const headings = sectionHeadings(order, meta.titles, (id) => !!byId.get(id)?.trim())
  const plan = planNumbering(order.map((id) => ({ id, markdown: byId.get(id) ?? "" })))
  let lastGroup: string | undefined
  const body = order.flatMap((id) => {
    const planned = plan.get(id) ?? []
    if (!planned.length) return []
    const h: SectionHeading = headings[id]
    const out: Block[] = []
    if (h.group && h.group !== lastGroup) out.push(headingBlock({ text: h.group, level: 1 }))
    lastGroup = h.group ?? lastGroup
    if (h.text) out.push(headingBlock(h))
    out.push(...planned.flatMap((b) => blockToDocx(id, b)))
    return out
  })
  const children = [...captionBlocks(meta), ...body]
  const noticee = (meta.noticee ?? meta.title).trim()

  return new Document({
    title: meta.title,
    styles: oioDocumentStyles(),
    numbering: oioLegalNumbering(),
    sections: [
      {
        properties: { page: OIO_PAGE },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: SIMULATED_BANNER,
                    font: FONT,
                    size: SIZES.FOOTER,
                    bold: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
                border: {
                  bottom: { color: "000000", space: 1, style: BorderStyle.SINGLE, size: 8 },
                },
              }),
              // D4: the running line of the actual order.
              new Paragraph({
                children: [new TextRun({ text: `OIO No. ${OIO_NO} / ${noticee}`, font: FONT, size: SIZES.FOOTER })],
                alignment: AlignmentType.RIGHT,
                spacing: { after: 120 },
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: SIMULATED_BANNER, font: FONT, size: SIZES.FOOTER, bold: true }),
                ],
                alignment: AlignmentType.CENTER,
                border: {
                  top: { color: "CCCCCC", space: 1, style: BorderStyle.SINGLE, size: 4 },
                },
                spacing: { before: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Page ", font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ children: [PageNumber.CURRENT], font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ text: " of ", font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], font: FONT, size: SIZES.FOOTER }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        },
        children,
      },
    ],
  })
}

/** Replaces characters that are invalid in filenames on common platforms. */
export function safeFilenamePart(title: string): string {
  return title.replace(/[\/\\:*?"<>|]/g, "-")
}

/** Builds the draft and saves it as `<title>-simulated-draft.docx`. */
export async function downloadOioSimDocx(sections: OioSimSection[], meta: OioSimDocMeta): Promise<void> {
  const blob = await Packer.toBlob(buildOioSimDocx(sections, meta))
  saveAs(blob, `${safeFilenamePart(meta.title)}-simulated-draft.docx`)
}
